import {
  businessService,
  type BusinessRelationshipBulkStatusUpdateRequest,
  type BusinessRelationshipBulkStatusUpdateResponse,
  type BusinessRelationshipCompaniesUpdateRequest,
  type BusinessRelationshipDetailResponse,
  type BusinessRelationshipRequest,
  type BusinessRelationshipResponse,
  type BusinessRelationshipUpdateRequest,
  type BusinessRelationshipUploadItem,
} from '@/entities/business/api/business.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const businessKeys = {
  all: ['business-relationships'] as const,
  lists: () => [...businessKeys.all, 'list'] as const,
  list: (search: BusinessRelationshipRequest, pageable: Pageable) =>
    [...businessKeys.lists(), search, pageable] as const,
  details: () => [...businessKeys.all, 'detail'] as const,
  detail: (id: number, drugCode?: string | null) =>
    [...businessKeys.details(), id, drugCode ?? ''] as const,
};

export interface BusinessListParams {
  search: BusinessRelationshipRequest;
  pageable: Pageable;
  /** false면 조회하지 않음 (처방 등록 다이얼로그에서 사용자 선택 전 등) */
  enabled?: boolean;
}

/** 거래처(영업 제휴) 목록 — 레거시 `clientInfo.js` */
export const useBusinessRelationshipList = ({
  search,
  pageable,
  enabled = true,
}: BusinessListParams) =>
  usePaginatedQuery<BusinessRelationshipRequest, BusinessRelationshipResponse>({
    queryKeyPrefix: businessKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      businessService.getBusinessRelationships(s, p),
    enabled,
  });

/** 거래처 상세 — 레거시 `clientInfoDetails.js` GET */
export const useBusinessRelationshipDetail = (
  relationPharmaCompanyId: number | null,
  drugCode?: string | null,
) =>
  useQuery({
    queryKey:
      relationPharmaCompanyId != null
        ? businessKeys.detail(relationPharmaCompanyId, drugCode)
        : [],
    queryFn: async () => {
      if (relationPharmaCompanyId == null) {
        throw new Error('relationPharmaCompanyId is required');
      }
      return businessService.getBusinessRelationshipDetail(
        relationPharmaCompanyId,
        drugCode ?? undefined,
      );
    },
    enabled: relationPharmaCompanyId != null,
  });

export const useUpdateBusinessRelationship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      relationPharmaCompanyId,
      data,
    }: {
      relationPharmaCompanyId: number;
      data: BusinessRelationshipUpdateRequest;
    }) => businessService.updateBusinessRelationship(relationPharmaCompanyId, data),
    onSuccess: (_, { relationPharmaCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.lists() });
      // drugCode 유무와 관계없이 해당 거래처 상세만 1회 갱신
      queryClient.invalidateQueries({
        queryKey: [...businessKeys.details(), relationPharmaCompanyId],
      });
    },
  });
};

/** 목록 선택 행 거래 상태 일괄 변경 — 부분 성공 응답 */
export const useBulkUpdateBusinessRelationshipTransactionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      request: BusinessRelationshipBulkStatusUpdateRequest,
    ): Promise<BusinessRelationshipBulkStatusUpdateResponse> =>
      businessService.updateBusinessRelationshipTransactionStatuses(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.lists() });
      queryClient.invalidateQueries({ queryKey: businessKeys.details() });
    },
  });
};

export const useUpdateBusinessRelationshipCompanies = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      relationPharmaCompanyId,
      data,
    }: {
      relationPharmaCompanyId: number;
      data: BusinessRelationshipCompaniesUpdateRequest;
    }) =>
      businessService.updateBusinessRelationshipCompanies(
        relationPharmaCompanyId,
        data,
      ),
    onSuccess: (_, { relationPharmaCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: businessKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: [...businessKeys.details(), relationPharmaCompanyId],
      });
    },
  });
};

/** 거래처 전체 CSV보내기 — 레거시 `clientInfo.js` download */
export const useExportBusinessRelationships = () =>
  useMutation({
    mutationFn: () => businessService.exportAllBusinessRelationships(),
  });

/** 거래처 엑셀/CSV 일괄 업로드 */
export const useUploadBusinessRelationships = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: BusinessRelationshipUploadItem[]) =>
      businessService.uploadBusinessRelationshipsFromExcel(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessKeys.lists() });
    },
  });
};

export type {
  BusinessRelationshipDetailResponse,
  BusinessRelationshipUpdateRequest,
};
